import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useProfileRoutesConditions, useProfileRoutesRequired } from '@pages/Profile/ProfileRoutes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { EconomicProfileIcon } from '@ui/icons/EconomicProfile.icon'
import { EditFileIcon } from '@ui/icons/EditFile.icon'
import { FileIcon } from '@ui/icons/File.icon'
import { GlobeIcon } from '@ui/icons/Globe.icon'
import { LogOutIcon } from '@ui/icons/LogOut.icon'
import { MobileIcon } from '@ui/icons/Mobile.icon'
import { SettingIcon } from '@ui/icons/Setting.icon'
import { ShieldIcon } from '@ui/icons/Shield.icon'
import { UserSquareIcon } from '@ui/icons/UserSquare.icon'

import { isSumsubNeeded, isSumsubSupported } from '@utils/AccountAccess/accountKYCStatuses'
import { hasClearedAppropriatenessTest } from '@utils/AccountAccess/accountProductStatuses'
import { isTickmillSC } from '@utils/companyName.utils'
import { BasePaths, CommonPaths, getProductEconomicProfilePath } from '@utils/path'

import { AccountDetailedStatus } from '@model/AccountDetailedStatus'

import { useHideSensitiveData } from '../../menuSections/topLevelMenus/useHideSensitiveData'
import { useLanguageMenu } from '../../menuSections/topLevelMenus/useLanguageMenu'
import { MenuSection } from '../../menuSections/useMenuSections'

/**
 * profile menu items for setting 2 (LayoutMinimalAuth).
 *
 * As WCA-5859, the profile menu content is displayed as 1st level items , then inside dropdown lang picker is 2nd-last from the last
 * and sign out is last
 * notifications are completely removed
 *
 * items depends by account status
 * - EmailVerified (TMEU/TMUK) -> appropriatenessTest, Language, SignOut
 * - AppTestSubmitted (TMSC + Sumsub needed) -> Verification, Language, SignOut
 * - AppTestSubmitted (TMSC + Sumsub submitted) -> Phone Numbers, Personal Details, Documents, Economic Profile (if TMEU+EP needed), Security Settings, Language, SignOut
 * - AppTestSubmitted (not passed) -> appropriatenessTest, Language, SignOut
 * - AppTestSubmitted (passed) -> Phone Numbers, Personal Details, Documents, Economic Profile (if TMEU+EP needed), Security Settings, Language, SignOut
 * - AppTestSubmitted (retake allowed) -> Phone Numbers, Appropriateness Test, Personal Details, Documents, Preferences, Security Settings, Language, SignOut
 * - Dormant / Closed -> Phone Numbers, Appropriateness Test, Personal Details, Documents, Preferences, Security Settings, HideSensitiveData, Language, SignOut
 * - PendingDocVerification / ValidationDenied / Blocked -> Phone Numbers, Personal Details, Documents, Preferences, Security Settings, HideSensitiveData, Language, SignOut
 */
export const useMinimalMenuItems = (): MenuSection[] => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()

  const {
    isDocManagementVisible,
    isSumsubVisible,
    isEconomicProfileVisible,
    isAppropriatenessTestVisible,
    isAppropriatenessTestRetakeAllowed,
    isUKProductAppropriatenessMenuVisible,
  } = useProfileRoutesConditions()
  const { shouldVerifyPhoneNumber, documentManagement } = useProfileRoutesRequired()

  const hideSensitiveData = useHideSensitiveData()
  const languageMenu = useLanguageMenu()

  const statusId = account?.status?.id

  return useMemo(() => {
    const appropriatenessTest: MenuSection = {
      label: t('Profile.Appropriateness Test'),
      id: 'appropriateness-test',
      path: BasePaths.TakeAppTest,
      condition: isAppropriatenessTestVisible,
      icon: EditFileIcon,
    }

    const signOut: MenuSection = {
      id: 'logout',
      label: t('Profile.Logout'),
      icon: LogOutIcon,
      path: '/logout',
    }

    const phoneNumbers: MenuSection = {
      id: 'minimal-phone-numbers',
      label: t('Profile.Phone Numbers'),
      path: '/profile/phone-numbers',
      alertActive: shouldVerifyPhoneNumber,
      icon: MobileIcon,
    }

    const personalDetails: MenuSection = {
      id: 'minimal-personal-details',
      label: t('Profile.Personal Details'),
      path: '/profile/personal-info',
      icon: UserSquareIcon,
    }

    const documents: MenuSection = {
      id: 'minimal-documents',
      label: t('Profile.Documents'),
      path: isSumsubVisible ? CommonPaths.Sumsub : CommonPaths.DocumentManagement,
      alertActive: documentManagement,
      condition: isDocManagementVisible || isSumsubVisible,
      icon: FileIcon,
    }

    const preferences: MenuSection = {
      id: 'minimal-preferences',
      label: t('Profile.Preferences'),
      path: '/profile/account-settings',
      icon: SettingIcon,
    }

    const securitySettings: MenuSection = {
      id: 'minimal-security-settings',
      label: t('Profile.Security Settings'),
      path: '/profile/security-settings',
      icon: ShieldIcon,
    }

    const lang: MenuSection = {
      ...languageMenu,
      id: 'minimal-language',
      icon: GlobeIcon,
    }

    const verification: MenuSection = {
      id: 'minimal-verification',
      label: t('Verification'),
      path: CommonPaths.Sumsub,
      alertActive: documentManagement,
      condition: isSumsubVisible,
      icon: CircleCheckIcon,
    }

    const economicProfile: MenuSection = {
      id: 'minimal-economic-profile',
      label: t('Economic Profile'),
      path: getProductEconomicProfilePath(product),
      condition: isEconomicProfileVisible,
      icon: EconomicProfileIcon,
    }

    // Base items for minimal statuses
    const baseMinimal = [appropriatenessTest, lang, signOut]

    switch (statusId) {
      case AccountDetailedStatus.EmailVerified:
        return baseMinimal

      case AccountDetailedStatus.AppropriatenessTestSubmitted: {
        const passed = hasClearedAppropriatenessTest(account, product)

        // TMSC client with Sumsub submitted - show full menu
        if (isTickmillSC(account) && isSumsubSupported(account) && account?.isPersonalInfoLocked) {
          return [
            phoneNumbers,
            personalDetails,
            documents,
            economicProfile,
            securitySettings,
            lang,
            signOut,
          ].filter((item) => item.condition !== false)
        }

        // TMSC client needing Sumsub verification flow
        if (isTickmillSC(account) && isSumsubNeeded(account)) {
          return [verification, lang, signOut].filter((item) => item.condition !== false)
        }

        if (!passed && !isAppropriatenessTestRetakeAllowed) {
          return baseMinimal
        }

        if (isAppropriatenessTestRetakeAllowed) {
          return [
            phoneNumbers,
            appropriatenessTest,
            personalDetails,
            documents,
            preferences,
            securitySettings,
            lang,
            signOut,
          ].filter((item) => item.condition !== false)
        }

        // Passed: TMEU client may need Economic Profile next
        return [
          phoneNumbers,
          personalDetails,
          documents,
          economicProfile,
          securitySettings,
          lang,
          signOut,
        ].filter((item) => item.condition !== false)
      }

      case AccountDetailedStatus.Activated: {
        if (isUKProductAppropriatenessMenuVisible) {
          return [
            phoneNumbers,
            appropriatenessTest,
            personalDetails,
            documents,
            preferences,
            securitySettings,
            hideSensitiveData,
            lang,
            signOut,
          ].filter((item) => item.condition !== false)
        }

        return [
          phoneNumbers,
          personalDetails,
          documents,
          economicProfile,
          securitySettings,
          lang,
          signOut,
        ].filter((item) => item.condition !== false)
      }

      case AccountDetailedStatus.Dormant:
      case AccountDetailedStatus.Closed:
        return [
          phoneNumbers,
          appropriatenessTest,
          personalDetails,
          documents,
          preferences,
          securitySettings,
          hideSensitiveData,
          lang,
          signOut,
        ].filter((item) => item.condition !== false)

      case AccountDetailedStatus.PendingDocumentVerification:
      case AccountDetailedStatus.ValidationDenied:
      case AccountDetailedStatus.Blocked:
        return [
          phoneNumbers,
          personalDetails,
          documents,
          preferences,
          securitySettings,
          hideSensitiveData,
          lang,
          signOut,
        ].filter((item) => item.condition !== false)

      default:
        return baseMinimal
    }
  }, [
    t,
    statusId,
    account,
    product,
    shouldVerifyPhoneNumber,
    documentManagement,
    isDocManagementVisible,
    isSumsubVisible,
    isEconomicProfileVisible,
    hideSensitiveData,
    languageMenu,
    isAppropriatenessTestVisible,
    isAppropriatenessTestRetakeAllowed,
    isUKProductAppropriatenessMenuVisible,
  ])
}
